import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import NotificationAlert from "../../../../components/ui/alerts/notifications/NotificationAlert";
import Breadcrumb from "../../../../components/ui/Breadcrumb";
import { StripePaymentIntentStatus } from "../../../../lib/Constants";
import FiInsightCard from "../../../private/financial-insights/common/FiInsightCard";
import {
  getSubscriptionSetupIntentStatus,
  updateSubscriptionStatus,
} from "../../../../api/payments/subscriptionsAPI";
import LoadingPlaceholder from "../../../../components/ui/loading/LoadingPlaceholder";
import { useDispatch } from "react-redux";
import { updateUserStatus } from "../../../../store/redux_auth/authSlice";
import { loadStripe } from "@stripe/stripe-js";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function CheckIntent({
  redirectStatus,
  service,
  purchaseId,
  searchParams,
  isUpgradeDowngradePlan = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [syncing, setSyncing] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [actionType, setActionType] = useState("subscribe");

  const dispatch = useDispatch();

  // Guard against React double-executing in StrictMode or state re-renders
  const hasVerified = useRef(false);

  const activeBreadcrumbs = [
    { label: "Financial Insights", to: "/finances/insights" },
    { label: "Verifying Payment" },
  ];

  useEffect(() => {
    // Prevent double verification runs
    if (hasVerified.current) return;
    hasVerified.current = true;

    const orchestrateVerification = async () => {
      if (redirectStatus !== StripePaymentIntentStatus.SUCCEEDED) {
        setErrorMessage(
          "Your transaction authorization was not successful. Please review your billing credentials and try again.",
        );
        setSyncing(false);
        return;
      }

      try {
        if (service === "FI") {
          const response = await getSubscriptionSetupIntentStatus(purchaseId);
          const data = await response.json();

          if (!response.ok) {
            throw new Error(
              data.message || "Failed to verify payment authorization.",
            );
          }

          if (data.setup_intent_status === "succeeded") {
            const setupIntentId = searchParams.get("setup_intent");

            // Dynamically resolve action from URL params, props, or location state
            const requestedAction =
              searchParams.get("action") ||
              location.state?.action ||
              (isUpgradeDowngradePlan || location.state?.isUpgradeDowngradePlan
                ? "upgrade"
                : "subscribe");

            const updateResponse = await updateSubscriptionStatus(purchaseId, {
              action: requestedAction,
              setup_intent_id: setupIntentId,
            });

            const updateData = await updateResponse.json();

            if (!updateResponse.ok) {
              throw new Error(
                updateData.message ||
                  "Failed to activate subscription records.",
              );
            }

            // Trust backend action_type return value first, with fallback to requestedAction
            const resolvedActionType =
              updateData.action_type || requestedAction;
            setActionType(resolvedActionType);

            if (updateData.requires_action === true) {
              if (!updateData.client_secret) {
                dispatch((dispatch, getState) => {
                  const currentInsights =
                    getState().auth.user?.financial_insights || {};
                  dispatch(
                    updateUserStatus({
                      financial_insights: {
                        ...currentInsights,
                        is_subscribed: true,
                        subscription_uuid: updateData.uuid,
                        is_max_plan: updateData.is_max_plan,
                      },
                    }),
                  );
                });
                setSyncing(false);
                return;
              }

              const stripeInstance = await stripePromise;
              if (!stripeInstance) {
                throw new Error("Stripe context could not be initialized.");
              }

              const clearAuthChallenge =
                await stripeInstance.confirmCardPayment(
                  updateData.client_secret,
                );

              if (clearAuthChallenge.error) {
                throw new Error(
                  clearAuthChallenge.error.message ||
                    "Payment authentication failed.",
                );
              }
              if (clearAuthChallenge.paymentIntent.status !== "succeeded") {
                throw new Error(
                  "The required invoice authorization could not be validated.",
                );
              }
            }

            // Sync Redux auth state safely via thunk without static state dependencies
            dispatch((dispatch, getState) => {
              const currentInsights =
                getState().auth.user?.financial_insights || {};
              dispatch(
                updateUserStatus({
                  financial_insights: {
                    ...currentInsights,
                    is_subscribed: true,
                    subscription_uuid: updateData.uuid,
                    is_max_plan: updateData.is_max_plan,
                  },
                }),
              );
            });

            setSyncing(false);
            return;
          }

          if (data.setup_intent_status === "processing") {
            throw new Error(
              "Your payment is still processing. Please refresh in a few moments.",
            );
          }

          throw new Error(
            "Payment authorization was not completed successfully.",
          );
        }
      } catch (error) {
        console.error("Verification orchestrator exception handler:", error);
        setErrorMessage(
          error.message ||
            "An unexpected communication exception occurred syncing transaction logs.",
        );
      } finally {
        setSyncing(false);
      }
    };

    orchestrateVerification();
  }, [
    redirectStatus,
    service,
    purchaseId,
    searchParams,
    dispatch,
    location.state,
    isUpgradeDowngradePlan,
  ]);

  const isPlanUpdate = actionType === "upgrade" || actionType === "downgrade";

  return (
    <div className="checkout-container">
      <Breadcrumb items={activeBreadcrumbs} />
      <div
        className="checkout-body"
        style={{ justifyContent: "center", paddingTop: "40px" }}
      >
        <div style={{ width: "100%", maxWidth: "600px" }}>
          {syncing && <LoadingPlaceholder rows={1} type="card-large" />}

          {!syncing && !errorMessage && (
            <div
              className="standard-card-container"
              style={{
                width: "100%",
                maxWidth: "600px",
                padding: "24px",
              }}
            >
              {isPlanUpdate ? (
                /* UPGRADE / DOWNGRADE SUCCESS VIEW */
                <>
                  <h3
                    style={{
                      marginTop: 0,
                      color: "#83b50d",
                      fontWeight: "400",
                      lineHeight: "140%",
                    }}
                  >
                    Subscription updated
                  </h3>
                  <p style={{ fontSize: "16px", color: "#40464b" }}>
                    Your Financial Insights plan has been successfully upgraded!
                  </p>
                  <FiInsightCard title="Take advantage of full features">
                    <div className="insight-item">
                      <span>
                        Connect more institutions to unlock deeper financial
                        analytics, track total cash flow across all accounts,
                        and maximize your insights.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "end",
                      }}
                    >
                      <Link
                        to="/finances/personal-finances/overview"
                        className="cta-button primary rounded"
                      >
                        Connect Institutions
                      </Link>
                    </div>
                  </FiInsightCard>
                </>
              ) : (
                /* INITIAL SUBSCRIPTION SUCCESS VIEW */
                <>
                  <h3
                    style={{
                      marginTop: 0,
                      color: "#83b50d",
                      fontWeight: "400",
                      lineHeight: "140%",
                    }}
                  >
                    Financial Insights unlocked
                  </h3>
                  <p style={{ fontSize: "16px", color: "#40464b" }}>
                    Thanks for subscribing! Financial Insights is now active.
                    Your first personalized insight is ready below.
                  </p>
                  <FiInsightCard title="Get Started">
                    <div className="insight-item">
                      <span>
                        Start by connecting your first institution and see how
                        your Money Flow becomes clear.
                      </span>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "end",
                      }}
                    >
                      <Link
                        to="/finances/personal-finances/overview"
                        className="cta-button primary rounded"
                      >
                        Connect Institution
                      </Link>
                    </div>
                  </FiInsightCard>
                </>
              )}
            </div>
          )}

          {errorMessage && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                width: "100%",
              }}
            >
              <NotificationAlert
                alertType="error"
                alertTitle="Transaction Verification Aborted"
                alertDescription={errorMessage}
              />
              <button
                className="cta-button primary"
                onClick={() => navigate(-1)}
                style={{ width: "100%" }}
              >
                Return and Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CheckIntent;
